import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import addDays from 'date-fns/addDays';
import { setCookie, parseCookies } from 'nookies';
import { useIntercom } from 'react-use-intercom';

import { __PROD__, INTERCOM_INITIALIZE_DELAY } from '@/utils/constants';

type ApplicationStateContextData = {
  acceptedCookies: boolean;
};

const ApplicationStateContext = createContext<ApplicationStateContextData>(
  {} as ApplicationStateContextData,
);

type ApplicationDispatchContextData = {
  saveCookieChoice: () => void;
};

const ApplicationDispatchContext =
  createContext<ApplicationDispatchContextData>(
    {} as ApplicationDispatchContextData,
  );

type ApplicationProviderProps = {
  children: ReactNode;
};

const ApplicationProvider = ({ children }: ApplicationProviderProps) => {
  const [acceptedCookies, setAcceptedCookies] = useState(true);
  const intercom = useIntercom();

  function saveCookieChoice() {
    setAcceptedCookies(!acceptedCookies);
    setCookie(null, `cookie-consent`, `${!acceptedCookies}`, {
      sameSite: `lax`,
      path: `/`,
      expires: addDays(new Date(), 180),
    });
  }

  function updateIntercomLauncher() {
    intercom.update({
      hideDefaultLauncher: !acceptedCookies,
    });
  }

  useEffect(() => {
    const { 'cookie-consent': hasCookieConsent } = parseCookies();

    if (!hasCookieConsent) setAcceptedCookies(false);

    /**
     * The timer is needed to boot Intercom only after the script is loaded.
     *
     * Intercom has an initialization delay of 5 seconds.
     * We need to wait for the script to load before we can boot it.
     *
     * The state isn't trustable here because it is initialized with true, and we
     * can't use the hasCookieConsent value because it may be inaccurate.
     * For example: if the page loads and the user hasn't accepted cookies, the
     * value will be undefined inside the timeout. But if the user accepts cookies
     * before the timeout runs (5 seconds), the cookie value is set as true, but
     * hasCookieConsent variable is still undefined. This is why we parse the cookie
     * again to get the correct value.
     */
    const intercomTimer = setTimeout(() => {
      const { 'cookie-consent': cookieConsent } = parseCookies();

      if (__PROD__) {
        intercom.boot({
          hideDefaultLauncher: !cookieConsent,
          customLauncherSelector: `.open_intercom`,
        });
      }
    }, INTERCOM_INITIALIZE_DELAY);

    return () => clearTimeout(intercomTimer);
  }, []);

  useEffect(() => {
    /**
     * When the `acceptedCookies` value changes, this code will run.
     *
     * If the Intercom script is already loaded, then we will hide or show the
     * bubble, depending on the state.
     *
     * Example: If the user accepts cookies after the initialization delay,
     * then this code runs and the Intercom bubble will show.
     */
    if (__PROD__ && window?.Intercom?.booted) {
      updateIntercomLauncher();
    }
  }, [acceptedCookies]);

  return (
    <ApplicationStateContext.Provider
      value={{
        acceptedCookies,
      }}
    >
      <ApplicationDispatchContext.Provider value={{ saveCookieChoice }}>
        {children}
      </ApplicationDispatchContext.Provider>
    </ApplicationStateContext.Provider>
  );
};

const useApplicationState = () => {
  const context = useContext(ApplicationStateContext);

  if (!context)
    throw new Error(
      `useApplicationState must be used within a ApplicationProvider`,
    );

  return context;
};

const useApplicationDispatch = () => {
  const context = useContext(ApplicationDispatchContext);

  if (!context)
    throw new Error(
      `useApplicationDispatch must be used within a ApplicationProvider`,
    );

  return context;
};

export { ApplicationProvider, useApplicationState, useApplicationDispatch };
